import { fetchSubscriptionInvoices } from 'api/SubscriptionApiService';
import ReusableIcon from 'components/common/ReusableIcon';
import SecondaryButton from 'components/common/SecondaryButton';
import SubscriptionInvoice from 'interface/SubscriptionInvoice'
import React, { useEffect } from 'react'
import styles from 'styles/components/Invoices.module.sass'

/**
 * Props for the Invoices component.
 */
interface InvoicesProps {
    status: 'draft' | 'open' | 'paid' | 'uncollectible' | 'void';
}

/**
 * Invoices component - Displays a list of invoices.
 *
 * This component displays a list of invoices that belong to the user.
 *
 * @component
 * @param {InvoicesProps} props - The properties for the Invoices component.
 * @param {'draft' | 'open' | 'paid' | 'uncollectible' | 'void'} props.status - The status of the invoices to be displayed.
 *
 * @example
 * // Example usage:
 * <Invoices status="draft" />
 *
 * @returns {JSX.Element} The rendered Invoices component.
 */
const Invoices: React.FC<InvoicesProps> = ({ status }) => {
    const [invoices, setInvoices] = React.useState<SubscriptionInvoice[]>([]);
    const [activeInvoices, setActiveInvoices] = React.useState<SubscriptionInvoice[]>([]);
    const [activePage, setActivePage] = React.useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        const fetchInvoices = async () => {
            try {
                // Fetch invoices
                const fetchedInvoices = await fetchSubscriptionInvoices({ tag: 'Invoices', status: status })
                setInvoices(fetchedInvoices)
            } catch {
                setInvoices([])
            }
        }

        fetchInvoices()
    }, [status]);

    useEffect(() => {
        setActivePage(1);
        setActiveInvoices(invoices.slice(0, itemsPerPage));
    }, [invoices]);

    const handlePageChange = (newPage: number) => {
        setActivePage(newPage);
        const startIndex = (newPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        setActiveInvoices(invoices.slice(startIndex, endIndex));
    }

    return (
        <div className={styles['invoices']}>
            <table className={styles['invoices-table']}>
                <thead className={styles['invoices-table-header']}>
                    <tr className={styles['invoices-table-row']}>
                        <th className={styles['invoices-table-header-cell']}>Invoice ID</th>
                        <th className={styles['invoices-table-header-cell']}>Description</th>
                        <th className={styles['invoices-table-header-cell']}>Amount</th>
                        <th className={styles['invoices-table-header-cell']}>Date</th>
                        <th className={styles['invoices-table-header-cell']}>Action</th>
                    </tr>
                </thead>
                <tbody className={styles['invoices-table-body']}>
                    {activeInvoices.map((invoice, index) => (
                        <tr key={index} className={styles['invoices-table-row']}>
                            <td className={styles['invoices-table-body-cell']}>{invoice.invoiceId}</td>
                            <td className={styles['invoices-table-body-cell']}>{invoice.description}</td>
                            <td className={styles['invoices-table-body-cell']}>{invoice.currency.toUpperCase()} {invoice.amount}</td>
                            <td className={styles['invoices-table-body-cell']}>{invoice.createdAt ? new Date(invoice.createdAt).toLocaleString('en-US') : ''}</td>
                            <td className={styles['invoices-table-body-cell']}>
                                <a href={invoice.receiptUrl ? invoice.receiptUrl : '#'} target="_blank" rel="noreferrer" className={styles['download-link'] || 'download-link'}>
                                    <ReusableIcon icon={'material-symbols-light:download-rounded'} className={styles['download-icon'] || 'download-icon'} color={'#1e1e1e'} />
                                </a>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            <div className={styles['invoice-controls']}>
                {(activePage > 1) && (
                    <SecondaryButton
                        text='Previous'
                        primaryColor="transparent"
                        textColor="#FF5522"
                        hoverColor="#FF5522"
                        hoverTextColor="#FFFFFF"
                        doSomething={() => { if (activePage > 1) handlePageChange(activePage - 1); }}
                    />
                )}
                <span className={styles['page-indicator']}>
                    Page {activePage} of {Math.ceil(invoices.length / itemsPerPage) || 1}
                </span>
                {(activePage < Math.ceil(invoices.length / itemsPerPage)) && (
                    <SecondaryButton
                        text='Next'
                        primaryColor="transparent"
                        textColor="#FF5522"
                        hoverColor="#FF5522"
                        hoverTextColor="#FFFFFF"
                        doSomething={() => { if (activePage < Math.ceil(invoices.length / itemsPerPage)) handlePageChange(activePage + 1); }}
                    />
                )}
            </div>
        </div>
    );
}

export default Invoices;